import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";

import { getMe } from "./api";
import { setAnalyticsAdminFlag } from "./lib/analytics";
import * as cognito from "./lib/cognito";
import { startGoogleSignIn } from "./lib/oauth";
import { clear, getValidIdToken, load, Session } from "./lib/session";

type Me = {
  email: string;
  free_credits: number;
  tokens: number;
  accepted_terms: boolean;
  terms_version: string;
  is_admin: boolean;
} | null;

type SessionCtx = {
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // re-read tokens from localStorage (e.g. after the OAuth callback writes new ones)
  refreshSession: () => Promise<void>;

  // user-balance (from API/me)
  me: Me;
  refreshMe: () => Promise<void>;

  // auth actions
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmation: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => void;

  // for callers: get a token, refreshing if needed
  token: () => Promise<string | null>;
};

const Ctx = createContext<SessionCtx | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => load());
  const [me, setMe] = useState<Me>(null);
  const [isLoading, setLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    const id = await getValidIdToken();
    setSession(id ? load() : null);
  }, []);

  const refreshMe = useCallback(async () => {
    const id = await getValidIdToken();
    if (!id) { setMe(null); setAnalyticsAdminFlag(false); return; }
    try {
      const m = await getMe(id);
      setMe(m);
      setAnalyticsAdminFlag(!!m?.is_admin);
    } catch { setMe(null); setAnalyticsAdminFlag(false); }
  }, []);

  useEffect(() => {
    (async () => {
      await refreshSession();
      await refreshMe();
      setLoading(false);
    })();
  }, [refreshSession, refreshMe]);

  const value = useMemo<SessionCtx>(() => ({
    session,
    isAuthenticated: !!session,
    isLoading,
    me,
    refreshMe,
    refreshSession,
    token: getValidIdToken,
    signIn: async (e, p) => { await cognito.signIn(e, p); await refreshSession(); await refreshMe(); },
    signUp: cognito.signUp,
    confirmSignUp: cognito.confirmSignUp,
    resendConfirmation: cognito.resendConfirmation,
    forgotPassword: cognito.forgotPassword,
    confirmForgotPassword: cognito.confirmForgotPassword,
    signInWithGoogle: startGoogleSignIn,
    signOut: () => {
      clear();
      setSession(null);
      setMe(null);
    },
  }), [session, isLoading, me, refreshSession, refreshMe]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession must be used inside SessionProvider");
  return v;
}

export const useMe = () => {
  const { me, refreshMe } = useSession();
  return { me, refresh: refreshMe };
};
